import { useEffect, useMemo, useState } from "react";
import { Play, Compass } from "lucide-react";
import { resolveMediaUrl, mediaProxyUrlForced, mediaUrl, type MediaRef } from "@/lib/media";
import Photo360Viewer from "@/components/Photo360Viewer";
import Video360Viewer from "@/components/Video360Viewer";
import ViewerErrorBoundary from "@/components/ViewerErrorBoundary";
import AiBadge from "@/components/AiBadge";

/**
 * SmartMedia rendert einen MediaRef abhängig vom tatsächlichen media_type,
 * den es via `/api/media/{id}/info` ermittelt. Damit können CMS-Felder
 * einheitlich als „Medium" angeboten werden, ohne dass jede Section-Renderer
 * Case-Logik enthalten muss.
 *
 * - photo       → <img>
 * - video       → <video> (autoplay/muted/loop-Defaults für Background-Kontexte)
 * - photo_360   → interactive=true: Photo360Viewer · sonst: Poster-<img> + 360°-Badge
 * - video_360   → interactive=true: Video360Viewer · sonst: Poster-<img> + 360°-Play-Badge
 *
 * Fehlt media_id (nur `path`), wird das Medium als klassisches Bild gerendert
 * (statischer Fallback / Legacy-Pfad).
 *
 * Der /info-Fetch wird im Modul-Cache gehalten (SessionStorage + In-Memory),
 * damit derselbe Media-Ref auf einer Seite nur einmal geladen wird.
 */

type SmartMediaRef = MediaRef;

type Props = {
    mediaRef?: SmartMediaRef;
    fallbackUrl?: string;
    alt?: string;
    className?: string;
    /** interaktiver 360°-Viewer statt Preview + Badge */
    interactive360?: boolean;
    videoAutoplay?: boolean;
    videoMuted?: boolean;
    videoLoop?: boolean;
    videoControls?: boolean;
    posterMediaRef?: SmartMediaRef;
    showTypeBadge?: boolean;
    onClick?: () => void;
    testId?: string;
    /** override für <img width/height> — beeinflusst Layout-Shift-Prävention */
    width?: number;
    height?: number;
    /** loading-Attribut für <img> */
    loading?: "lazy" | "eager";
    /** Instant-Immersion: Photo360Viewer startet direkt nach Ready mit
     *  Auto-Tour (Yaw-Rotation). Nur relevant bei interactive360 + photo_360. */
    autoTour360?: boolean;
    /** Instant-Immersion: Video360Viewer startet WebGL + play() direkt
     *  beim Mount (stumm). Nur relevant bei interactive360 + video_360. */
    autoPlay360?: boolean;
};

type InfoResp = {
    id: string;
    media_type?: string;
    mime?: string;
    poster_url?: string | null;
    stored_url?: string | null;
    width?: number | null;
    height?: number | null;
    hls_ready?: boolean;
    hls_playlist_url?: string | null;    codec?: string | null;
    is_browser_compatible?: boolean;
    size_bytes?: number | null;
    is_ai_generated?: boolean;
};
const infoCache = new Map<string, InfoResp | null>();

async function fetchInfo(mediaId: string): Promise<InfoResp | null> {
    if (infoCache.has(mediaId)) return infoCache.get(mediaId) ?? null;
    try {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const res = await fetch(`${base}/api/media/${mediaId}/info`, { credentials: "omit" });
        if (!res.ok) {
            infoCache.set(mediaId, null);
            return null;
        }
        const data = (await res.json()) as InfoResp;
        infoCache.set(mediaId, data);
        return data;
    } catch {
        infoCache.set(mediaId, null);
        return null;
    }
}

function extractMediaId(ref: SmartMediaRef): string | undefined {
    if (!ref) return undefined;
    if (typeof ref === "string") return undefined;
    return ref.media_id;
}

function TypeBadge({ label }: { label: string }) {
    return (
        <span
            className="absolute z-10 top-3 left-3 text-[9px] md:text-[10px] tracking-[0.22em] uppercase px-2 py-1 rounded-sm inline-flex items-center gap-1.5 pointer-events-none"
            style={{
                background: "hsl(var(--hf-ink) / 0.78)",
                color: "hsl(var(--hf-gold-soft))",
                border: "1px solid hsl(var(--hf-gold-dark) / 0.5)",
            }}
        >
            <Compass size={10} aria-hidden />
            {label}
        </span>
    );
}

function PlayOverlay() {
    return (
        <span
            className="absolute z-10 inset-0 flex items-center justify-center pointer-events-none"
            aria-hidden
        >
            <span
                className="inline-flex items-center justify-center w-14 h-14 rounded-full transition-transform"
                style={{
                    background: "hsl(var(--hf-gold) / 0.92)",
                    color: "hsl(var(--hf-ink))",
                    boxShadow: "0 6px 24px hsl(var(--hf-ink) / 0.25)",
                }}
            >
                <Play size={22} fill="currentColor" />
            </span>
        </span>
    );
}

/**
 * Visueller Fallback wenn ein Video weder Poster noch abspielbare Quelle hat.
 * Verhindert leere weiße Kacheln mit sichtbarem Alt-Text im Live-Frontend.
 * Zeigt einen zurückhaltenden Ink-Ton mit Play-Icon + Label „Vorschau folgt".
 */
function MediaPlaceholder({
    label,
    className,
    testId,
}: {
    label: string;
    className?: string;
    testId?: string;
}) {
    return (
        <div
            className={`relative overflow-hidden flex items-center justify-center ${className || ""}`}
            style={{
                background:
                    "linear-gradient(135deg, hsl(var(--hf-ink)) 0%, hsl(var(--hf-surface)) 100%)",
                color: "hsl(var(--hf-gold-soft))",
                minHeight: 120,
            }}
            data-testid={testId || "smartmedia-placeholder"}
            aria-hidden
        >
            <span className="flex flex-col items-center gap-3 opacity-80">
                <span
                    className="inline-flex items-center justify-center w-12 h-12 rounded-full"
                    style={{
                        background: "hsl(var(--hf-gold) / 0.18)",
                        border: "1px solid hsl(var(--hf-gold-dark) / 0.4)",
                    }}
                >
                    <Play size={18} fill="currentColor" />
                </span>
                <span
                    className="text-[10px] tracking-[0.24em] uppercase"
                    style={{ color: "hsl(var(--hf-gold-soft))" }}
                >
                    {label}
                </span>
            </span>
        </div>
    );
}

export default function SmartMedia({
    mediaRef,
    fallbackUrl,
    alt,
    className,
    interactive360 = false,
    videoAutoplay = true,
    videoMuted = true,
    videoLoop = true,
    videoControls = false,
    posterMediaRef,
    showTypeBadge = true,
    onClick,
    testId,
    width,
    height,
    loading = "lazy",
    autoTour360,
    autoPlay360,
}: Props) {
    const mediaId = extractMediaId(mediaRef);
    // Sofort-Werte für Fallback-Rendering
    const initialUrl = useMemo(() => resolveMediaUrl(mediaRef, fallbackUrl), [mediaRef, fallbackUrl]);
    // WICHTIG: `posterUrl` nur befüllen, wenn der Caller EXPLIZIT ein
    // posterMediaRef übergibt. Sonst würde resolveMediaUrl auf `fallbackUrl`
    // zurückfallen — was den Backend-generierten `poster_url` des aktuellen
    // Media-Docs (siehe `posterFromInfo` unten) überschreibt und der User
    // sieht dauerhaft das statische Sektions-Fallback statt seines Videos.
    const posterUrl = useMemo(
        () => (posterMediaRef ? resolveMediaUrl(posterMediaRef, "") : ""),
        [posterMediaRef],
    );
    const [info, setInfo] = useState<InfoResp | null | undefined>(
        mediaId ? infoCache.get(mediaId) : null,
    );

    useEffect(() => {
        if (!mediaId) return;
        if (infoCache.has(mediaId)) {
            setInfo(infoCache.get(mediaId) ?? null);
            return;
        }
        let cancelled = false;
        (async () => {
            const data = await fetchInfo(mediaId);
            if (!cancelled) setInfo(data);
        })();
        return () => {
            cancelled = true;
        };
    }, [mediaId]);

    const wrapperProps = onClick
        ? { onClick, role: "button" as const, tabIndex: 0, style: { cursor: "pointer" } }
        : {};

    // Kein media_id (nur path) oder /info noch nicht geantwortet → klassisches <img>
    // (schneller Fallback, kein Skeleton-Flackern für Legacy-Pfade)
    if (!mediaId) {
        if (!initialUrl) return null;
        return (
            <img
                src={initialUrl}
                alt={alt || ""}
                width={width}
                height={height}
                loading={loading}
                className={className}
                data-testid={testId}
                {...wrapperProps}
            />
        );
    }

    // /info-Antwort ausstehend → dünnes Skeleton-<div> (kein <img> mit
    // src-Swap!). React würde sonst erst `initialUrl` (`/api/media/{id}` mit
    // 302-Redirect) rendern und dann nach fetchInfo auf `directUrl`
    // (`stored_url`) swappen. Dieser src-Swap kann bei `loading="lazy"` in
    // Chromium einen `net::ERR_FAILED` auslösen und das Bild bleibt leer.
    // Ein einziger stabiler <img> im mediaType-Branch unten löst das Problem.
    if (info === undefined) {
        return (
            <div
                className={`relative overflow-hidden ${className || ""}`}
                style={{ background: "hsl(var(--hf-surface))" }}
                data-testid={testId ? `${testId}-loading` : undefined}
                aria-hidden
                {...wrapperProps}
            />
        );
    }

    const mediaType = info?.media_type || "photo";
    // Für Fotos: bevorzugt die direkte R2-URL aus /info (`stored_url`).
    // Das umgeht den `/api/media/{id}`-302-Redirect, der bei `<img loading="lazy">`
    // in manchen Browsern (Chromium) nicht zuverlässig gefolgt wird und dann
    // eine leere Kachel hinterlässt.
    const directUrl = info?.stored_url || mediaUrl(mediaId);
    const posterFromInfo = info?.poster_url || undefined;
    // Effective poster: explizit übergebener posterMediaRef → Backend-Poster
    // → NUR wenn beides fehlt: statischer fallbackUrl (letzte Instanz).
    // Damit gewinnt der zum aktuellen Media-Doc gehörende Poster gegen jedes
    // Sektions-Fallback.
    const effectivePoster = posterUrl || posterFromInfo || fallbackUrl || "";
    // KI-Kennzeichnung: nur wenn `info.is_ai_generated===true`. Wrapper mit
    // `relative` wird nur eingezogen, wenn tatsächlich ein Badge gerendert
    // werden muss — sonst bleibt das bisherige Layout bit-perfekt erhalten
    // (kritisch für Aufrufer wie Hero-Video, die auf direktes `<img>` bauen).
    const isAi = info?.is_ai_generated === true;
    const aiBadge = isAi ? (
        <AiBadge testId={`ai-badge-${mediaId}`} />
    ) : null;

    // ── PHOTO ─────────────────────────────────────────
    if (mediaType === "photo") {
        const img = (
            <img
                src={directUrl}
                alt={alt || ""}
                width={width}
                height={height}
                loading={loading}
                className={className}
                data-testid={testId}
                {...wrapperProps}
            />
        );
        if (!isAi) return img;
        return (
            <span className={`relative inline-block ${className || ""}`}>
                <img
                    src={directUrl}
                    alt={alt || ""}
                    width={width}
                    height={height}
                    loading={loading}
                    className={className}
                    data-testid={testId}
                    {...wrapperProps}
                />
                {aiBadge}
            </span>
        );
    }

    // ── VIDEO (klassisch, flach) ──────────────────────
    if (mediaType === "video") {
        const vid = (
            <video
                src={directUrl}
                poster={effectivePoster || undefined}
                autoPlay={videoAutoplay}
                muted={videoMuted}
                loop={videoLoop}
                controls={videoControls}
                playsInline
                width={width}
                height={height}
                className={className}
                data-testid={testId}
                {...wrapperProps}
            />
        );
        if (!isAi) return vid;
        return (
            <div className={`relative ${className || ""}`} style={{ display: "inline-block" }}>
                {vid}
                {aiBadge}
            </div>
        );
    }

    // ── PHOTO_360 ─────────────────────────────────────
    if (mediaType === "photo_360") {
        if (interactive360) {
            // WICHTIG: erzwungener Same-Origin-Proxy. Direkte R2-URLs (aus
            // path-Feld) führen zu CORS-Fehlern beim Canvas-Convert im
            // Photo Sphere Viewer und triggern den ViewerFallback.
            const proxySrc = mediaProxyUrlForced(mediaId);
            // Poster-conditional Auto-Tour:
            //  - Media hat poster_url gesetzt  → statisches Cover, Klick zum
            //    Aktivieren. KEIN Auto-Tour.
            //  - Media hat KEIN poster_url    → Instant-Immersion: langsame
            //    Yaw-Rotation direkt nach Ready.
            // `autoTour360`-Prop kann das explizit übersteuern (force true/false).
            const hasPoster = Boolean(info?.poster_url);
            const shouldAutoTour = autoTour360 ?? !hasPoster;
            return (
                <div className={`relative ${className || ""}`} data-testid={testId}>
                    <ViewerErrorBoundary testId={`${testId || "smartmedia"}-fallback`}>
                        <Photo360Viewer
                            src={proxySrc}
                            autoTour={shouldAutoTour}
                            mediaId={mediaId}
                        />
                    </ViewerErrorBoundary>
                    {aiBadge}
                </div>
            );
        }
        return (
            <div className={`relative overflow-hidden ${className || ""}`} data-testid={testId} {...wrapperProps}>
                <img
                    src={directUrl}
                    alt={alt || ""}
                    width={width}
                    height={height}
                    loading={loading}
                    className="w-full h-full object-cover"
                />
                {showTypeBadge && <TypeBadge label="360° Foto" />}
                {aiBadge}
            </div>
        );
    }

    // ── VIDEO_360 ─────────────────────────────────────
    if (mediaType === "video_360") {
        if (interactive360) {
            // Poster-conditional Auto-Play:
            //  - Media hat poster_url gesetzt  → Poster + „Erlebnis starten"-Klick
            //  - Media hat KEIN poster_url    → sofort WebGL-Init + video.play()
            //    (muted, playsInline). Browser-Autoplay-Fallback greift bei Block.
            // `autoPlay360`-Prop kann das explizit übersteuern.
            const hasPoster = Boolean(info?.poster_url);
            const shouldAutoPlay = autoPlay360 ?? !hasPoster;
            const shouldAutoTour = autoTour360 ?? true;
            const apiBase = process.env.REACT_APP_BACKEND_URL || "";
            // Codec-basierte HLS-Entscheidung.
            //
            // Frühere Version: HLS wurde IMMER benutzt wenn `hls_ready=true`.
            // Das war für HEVC/5.7K nötig, aber für die häufigsten Standard-
            // Videos (H.264 1080p/4K) eine Latenz-Regression:
            //  - Direct MP4 startet in ~100-300 ms (Byte-Range-Fetch).
            //  - HLS braucht erst Manifest + erstes Segment (~2-6 s Länge)
            //    → 800-2000 ms bis Playback.
            //
            // Neue Regel — HLS nur wenn:
            //   (a) Codec ist im Browser nicht dekodierbar (HEVC, H.265)
            //   (b) Datei > 200 MB (Bandwidth-Adaptivität lohnt)
            //   (c) Breite > 4096 px (echte 4K+/8K → adaptive Downscale)
            // In allen anderen Fällen: Direct MP4 (deutlich schneller).
            const sizeBytes = info?.size_bytes || 0;
            const width = info?.width || 0;
            const codecOK = info?.is_browser_compatible !== false;
            const needsHls =
                info?.hls_ready &&
                (!codecOK || sizeBytes > 200 * 1024 * 1024 || width > 4096);
            const hlsSrc =
                needsHls && info?.hls_playlist_url
                    ? `${apiBase}${info.hls_playlist_url}`
                    : undefined;
            // Poster für Layer-1-DOM-Overlay (`<img>`) — HTML img hat keine
            // CORS-Einschränkungen, deshalb nutzen wir direkt die R2-URL für
            // schnellstmöglichen Load (CDN-cached, ~50-200 ms). Die WebGL-
            // Sphere lädt keine Poster-Textur mehr, deshalb ist der proxy-
            // Umweg nicht mehr nötig.
            const posterForOverlay = info?.poster_url || effectivePoster || "";
            return (
                <div className={`relative ${className || ""}`} data-testid={testId}>
                    <ViewerErrorBoundary testId={`${testId || "smartmedia"}-fallback`}>
                        <Video360Viewer
                            src={directUrl}
                            hlsSrc={hlsSrc}
                            poster={posterForOverlay}
                            testIdPrefix={testId || "smartmedia-v360"}
                            autoPlay={shouldAutoPlay}
                            autoTour={shouldAutoTour}
                            mediaId={mediaId}
                        />
                    </ViewerErrorBoundary>
                    {aiBadge}
                </div>
            );
        }
        // Preview: silent-loop-Video, damit man den 360°-Effekt „ahnt" — plus Badge.
        // Wenn KEIN Poster verfügbar ist UND `videoAutoplay` deaktiviert wurde,
        // greift der `MediaPlaceholder`, damit keine leere Kachel entsteht.
        if (!effectivePoster && !videoAutoplay) {
            return (
                <div className={`relative overflow-hidden ${className || ""}`} data-testid={testId} {...wrapperProps}>
                    <MediaPlaceholder
                        label="360°-Video · Vorschau folgt"
                        className="w-full h-full"
                        testId={`${testId || "smartmedia"}-placeholder`}
                    />
                    {showTypeBadge && <TypeBadge label="360° Video" />}
                    {onClick && <PlayOverlay />}
                    {aiBadge}
                </div>
            );
        }
        return (
            <div className={`relative overflow-hidden ${className || ""}`} data-testid={testId} {...wrapperProps}>
                <video
                    src={directUrl}
                    poster={effectivePoster || undefined}
                    autoPlay={videoAutoplay}
                    muted
                    loop={videoLoop}
                    playsInline
                    className="w-full h-full object-cover"
                />
                {showTypeBadge && <TypeBadge label="360° Video" />}
                {onClick && <PlayOverlay />}
                {aiBadge}
            </div>
        );
    }

    // Unbekannter Type → fallback img
    return (
        <img
            src={initialUrl}
            alt={alt || ""}
            width={width}
            height={height}
            loading={loading}
            className={className}
            data-testid={testId}
        />
    );
}
